
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', id: 'hero', icon: 'ri-home-line' },
    { name: 'About', id: 'about', icon: 'ri-information-line' },
    { name: 'Products', id: 'products', icon: 'ri-shopping-bag-line' },
    { name: 'Services', id: 'services', icon: 'ri-service-line' },
    { name: 'Projects', id: 'gallery', icon: 'ri-gallery-line' },
    
  ];

  return (
    <>
      <motion.header 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'py-2 bg-gradient-to-r from-[#225a96]/95 via-[#1a4670]/95 to-[#0f2d47]/95 backdrop-blur-xl shadow-lg border-b border-white/10' 
            : 'py-3 bg-gradient-to-r from-[#225a96]/90 via-[#1a4670]/85 to-[#0f2d47]/90 backdrop-blur-lg shadow-md border-b border-white/5'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center space-x-2 cursor-pointer group"
              onClick={() => scrollToSection('hero')}
            >
              <motion.div
                className=""
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(255, 255, 255, 0.1), 0 0 20px rgba(255, 255, 255, 0.05)",
                }}
              >
                <motion.img
                  src="/assets/logo-final.png"
                  alt="Prime Edge Logo"
                  className={`transition-all duration-500 object-cover w-[125px] md:w-[150px] ${isScrolled ? 'h-14' : 'h-14'} group-hover:brightness-125 filter drop-shadow-lg`}
                  whileHover={{ rotate: [0, -5, 5, 0] }}
                  transition={{ duration: 0.6 }}
                />
              </motion.div>
             
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: "rgba(34, 90, 150, 0.2)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection(item.id)}
                  className="relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 text-[#f5f5f5] hover:text-white backdrop-blur-sm glass-effect border border-transparent hover:border-white/10 text-sm whitespace-nowrap"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  <motion.i 
                    className={`${item.icon} text-sm text-[#d02b37]`}
                    whileHover={{ scale: 1.2, rotate: 180 }}
                    transition={{ duration: 0.5 }}
                  />
                  <span>{item.name}</span>
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] scale-x-0 origin-left rounded-full"
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
              ))}
              
              <motion.button
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                whileHover={{ 
                  scale: 1.05,
                  backgroundColor: "rgba(34, 90, 150, 0.25)",
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('contact')}
                className="relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 text-white glass-effect border border-[#225a96]/30 hover:border-[#225a96]/50 text-sm whitespace-nowrap"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                <motion.i 
                  className="ri-phone-line text-sm text-[#d02b37]"
                  whileHover={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 0.5 }}
                />
                <span>Contact</span>
              </motion.button>
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 25px rgba(208, 43, 55, 0.4), 0 0 20px rgba(208, 43, 55, 0.2)",
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('contact')}
                className="relative overflow-hidden bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] hover:from-[#ff6b7a] hover:to-[#d02b37] text-white px-6 py-2 rounded-xl font-bold transition-all duration-500 flex items-center space-x-2 shadow-lg whitespace-nowrap text-sm"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500"
                />
                <motion.i 
                  className="ri-phone-line relative z-10 text-sm"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="relative z-10">Get Quote</span>
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              whileTap={{ scale: 0.98 }}
              whileHover={{ 
                backgroundColor: "rgba(34, 90, 150, 0.2)",
              }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 glass-effect rounded-xl flex items-center justify-center text-white border border-white/10 transition-all duration-300"
            >
              <motion.i 
                className={isMobileMenuOpen ? 'ri-close-line text-lg' : 'ri-menu-line text-lg'}
                animate={{ rotate: isMobileMenuOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <motion.div 
        initial={{ opacity: 0, y: -100 }}
        animate={{ 
          opacity: isMobileMenuOpen ? 1 : 0,
          y: isMobileMenuOpen ? 0 : -100,
          pointerEvents: isMobileMenuOpen ? 'auto' : 'none'
        }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 left-0 right-0 z-40 lg:hidden"
      >
        <div className="bg-gradient-to-br from-[#225a96]/95 via-[#1a4670]/95 to-[#0f2d47]/95 backdrop-blur-xl pt-20 pb-6 px-4 shadow-xl border-b border-white/10">
          <nav className="container mx-auto">
            <div className="grid grid-cols-2 gap-4 mb-6">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.name}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ 
                    opacity: isMobileMenuOpen ? 1 : 0,
                    scale: isMobileMenuOpen ? 1 : 0.8,
                    y: isMobileMenuOpen ? 0 : 20
                  }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: "rgba(34, 90, 150, 0.2)",
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection(item.id)}
                  className="p-4 rounded-2xl font-medium transition-all duration-300 flex flex-col items-center space-y-2 text-[#f5f5f0] hover:text-white glass-effect border border-white/10 hover:border-[#d02b37]/30"
                  style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                >
                  <motion.i 
                    className={`${item.icon} text-2xl text-[#d02b37]`}
                    whileHover={{ scale: 1.2, rotate: 180 }}
                    transition={{ duration: 0.5 }}
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </motion.button>
              ))}
              
              <motion.button
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ 
                  opacity: isMobileMenuOpen ? 1 : 0,
                  scale: isMobileMenuOpen ? 1 : 0.8,
                  y: isMobileMenuOpen ? 0 : 20
                }}
                transition={{ duration: 0.3, delay: 0.5 }}
                whileHover={{ 
                  scale: 1.05,
                  backgroundColor: "rgba(34, 90, 150, 0.25)",
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => scrollToSection('contact')}
                className="p-4 rounded-2xl font-medium transition-all duration-300 flex flex-col items-center space-y-2 text-white glass-effect border border-[#225a96]/30"
                style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
              >
                <motion.i 
                  className="ri-phone-line text-2xl text-[#225a96]"
                  whileHover={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 0.5 }}
                />
                <span className="text-sm font-medium">Contact</span>
              </motion.button>
            </div>

            <motion.button
              initial={{ opacity: 0, y: 30 }}
              animate={{ 
                opacity: isMobileMenuOpen ? 1 : 0,
                y: isMobileMenuOpen ? 0 : 30
              }}
              transition={{ duration: 0.4, delay: 0.6 }}
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 10px 25px rgba(208, 43, 55, 0.4), 0 0 20px rgba(208, 43, 55, 0.2)",
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => scrollToSection('contact')}
              className="w-full relative overflow-hidden bg-gradient-to-r from-[#d02b37] to-[#ff6b7a] hover:from-[#ff6b7a] hover:to-[#d02b37] text-white py-4 rounded-2xl font-bold flex items-center justify-center space-x-3 shadow-lg whitespace-nowrap transition-all duration-500"
              style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500"
              />
              <motion.i 
                className="ri-phone-line text-lg relative z-10"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="relative z-10">Get Quote Now</span>
            </motion.button>
          </nav>
        </div>
      </motion.div>
    </>
  );
}
